import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { DateRangePicker } from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import ShowReceipt from "./ShowReceipt";

const TokenList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get parameters from navigation state or use defaults
  const { categoryId, dateFrom, dateTo } = location.state || {};
  
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    categoryId: categoryId || null,
    dateFrom: dateFrom || moment().startOf('day').utc().toISOString(),
    dateTo: dateTo || moment().endOf('day').utc().toISOString(),
  });
  const [showClearButton, setShowClearButton] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [selectedToken, setSelectedToken] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const apiBase = 
    process.env.REACT_APP_BASEURL || 
    window.__APP_CONFIG__?.API_BASE_URL || "";

  useEffect(() => {
    if (filters.categoryId) {
      fetchTokenList();
    }
  }, [filters]);

  const fetchTokenList = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.categoryId) params.append("categoryId", filters.categoryId);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);

      const response = await axios.get(
        `${apiBase}api/v1/Order/TokenListByCategory?${params.toString()}`
      );

      setTokenData(response.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching token list:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (start, end) => {
    setFilters({
      ...filters,
      dateFrom: moment(start).startOf('day').utc().toISOString(),
      dateTo: moment(end).endOf('day').utc().toISOString(),
    });
    setShowClearButton(true);
  };

  const clearDateFilter = () => {
    const defaultDateFrom = moment().startOf('day').utc().toISOString();
    const defaultDateTo = moment().endOf('day').utc().toISOString();

    setFilters({
      ...filters,
      dateFrom: defaultDateFrom,
      dateTo: defaultDateTo,
    });

    setShowClearButton(false);
    setDatePickerKey(prev => prev + 1);
  };

  const handleBackToDashboard = () => {
    navigate("/index"); 
  };

  const handleShowReceipt = async (token) => {
    try {
      setReceiptLoading(true);
      setSelectedToken(token);
      
      // Call the receipt API
      const response = await axios.get(
        `${apiBase}api/v1/Order/ReceiptByToken`,
        { params: { tokenNumber: token.tokenNumber } }
      );
      
      setSelectedToken(prev => ({
        ...prev,
        receiptData: response.data
      }));
      
      setShowReceipt(true);
    } catch (err) {
      console.error("Error fetching receipt:", err);
      alert(`Error loading receipt: ${err.message}`);
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedToken(null);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("YYYY/MM/DD HH:mm");
  };

  // Helper function to safely get the token date
//   const getTokenDate = (token) => {
//     return token.createdAt || token.orderDate || token.dateCreated || new Date().toISOString();
//   };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger">
            Error loading token data: {error}
            <button className="btn btn-sm btn-outline-primary ms-2" onClick={handleBackToDashboard}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Section */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
          <div>
            <button 
              className="btn btn-outline-secondary mb-2"
              onClick={handleBackToDashboard}
            >
              <i className="ti ti-arrow-left me-2"></i>Back 
            </button>
          </div>
          
          <div className="d-flex gap-2 align-items-center">
            <div className="input-icon-start position-relative">
              <DateRangePicker
                key={datePickerKey}
                initialSettings={{
                  startDate: moment(filters.dateFrom).toDate(),
                  endDate: moment(filters.dateTo).toDate(),
                  ranges: {
                    Today: [moment().toDate(), moment().toDate()],
                    Yesterday: [
                      moment().subtract(1, "days").toDate(),
                      moment().subtract(1, "days").toDate(),
                    ],
                    "Last 7 Days": [
                      moment().subtract(6, "days").toDate(),
                      moment().toDate(),
                    ],
                    "Last 30 Days": [
                      moment().subtract(29, "days").toDate(),
                      moment().toDate(),
                    ],
                    "This Month": [
                      moment().startOf("month").toDate(),
                      moment().endOf("month").toDate(),
                    ],
                    "Last Month": [
                      moment().subtract(1, "month").startOf("month").toDate(),
                      moment().subtract(1, "month").endOf("month").toDate(),
                    ],
                  },
                }}
                onCallback={handleDateRangeChange}
              >
                <div
                  className="form-control"
                  style={{
                    cursor: "pointer",
                    background: "#fff",
                    minWidth: "250px",
                    height: "38px",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <i className="ti ti-calendar me-2"></i>
                  <span>
                    {moment(filters.dateFrom).format("YYYY/MM/DD")} - {moment(filters.dateTo).format("YYYY/MM/DD")}
                  </span>
                </div>
              </DateRangePicker>
            </div>
            
            {showClearButton && (
              <button
                className="btn btn-sm"
                onClick={clearDateFilter}
                style={{ height: '38px', padding: '0px' }}
                title="Clear date filter"
              >
                <i className="ti ti-refresh"></i>
              </button>
            )}
          </div>
        </div>
        
        {/* Receipt Loading Overlay */}
        {receiptLoading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading receipt...</span>
            </div>
          </div>
        )}

        {/* Category Info */}
        {tokenData?.categoryName && (
          <div className="row mb-1">
            <div className="col-12">
              <div className="card bg-light">
                <div className="card-body py-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-tag text-primary fs-4 me-3"></i>
                    <div>
                      <h5 className="card-title mb-0">{tokenData.categoryName}</h5>
                      <p className="text-muted mb-0">
                        {tokenData.tokens?.length || 0} tokens found
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Token List Table */}
        <div className="row">
          <div className="col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header">
                <h5 className="card-title mb-0">Token Details</h5>
              </div>
              <div className="card-body">
                {tokenData?.tokens && tokenData.tokens.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover table-striped">
                      <thead className="table-light">
                        <tr>
                          <th>Token Number</th>
                          <th>Order Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tokenData.tokens.map((token, index) => (
                          <tr key={index}>
                            <td>
                              <strong className="text-primary">#{token.tokenNumber}</strong>
                            </td>
                            <td>{formatDate(token.createdAt)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleShowReceipt(token)}
                                title="View Receipt"
                                disabled={receiptLoading}
                              >
                                <i className="ti ti-receipt me-1"></i>
                                Show Receipt
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i className="ti ti-package fs-48 text-muted mb-3"></i>
                    <h5 className="text-muted">No Tokens Found</h5>
                    <p className="text-muted">
                      No tokens available for the selected category and date range.
                    </p>
                    <button 
                      className="btn btn-primary mt-2"
                      onClick={clearDateFilter}
                    >
                      Try Different Date Range
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceipt && selectedToken && (
        <ShowReceipt 
          token={selectedToken} 
          onClose={handleCloseReceipt} 
        />
      )}

      <div className="copyright-footer d-flex align-items-center justify-content-between border-top bg-white gap-3 flex-wrap mt-4">
        <p className="fs-13 text-gray-9 mb-0">
          2025 © Belinnov Solutions. All Right Reserved
        </p>
      </div>

      <style>
        {`
          .card-icon {
            width: 50px;
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .table th {
            border-top: none;
            font-weight: 600;
          }
          .table td {
            vertical-align: middle;
          }
          .btn-sm {
            padding: 0.25rem 0.5rem;
            font-size: 0.875rem;
          }
        `}
      </style>
    </div>
  );
};

export default TokenList;